import type { Theme, SxProps } from '@mui/material/styles';

import { Navigate } from 'react-router';

import { type RoleAction, ORGANIZATION_ROLES } from 'src/auth/role-matrix';

import { useRole } from '../hooks/use-role';
import { RoleBasedGuard } from './role-based-guard';

// ----------------------------------------------------------------------

export type RoleGuardProps = {
  /** The §4.1 capability the page needs. */
  action: RoleAction;
  children: React.ReactNode;
  /** Send the person elsewhere instead of showing the permission-denied page. */
  redirectTo?: string;
  sx?: SxProps<Theme>;
};

/**
 * Route-level gate: renders the existing permission-denied page (RoleBasedGuard) when the current role may not
 * perform `action`. While the role is still loading nothing is rendered rather than a flash of either state.
 */
export function RoleGuard({ action, children, redirectTo, sx }: RoleGuardProps) {
  const { role, isLoading, can } = useRole();

  if (isLoading) {
    return null;
  }

  if (!can(action)) {
    if (redirectTo) {
      return <Navigate to={redirectTo} replace />;
    }

    // RoleBasedGuard compares names; feeding it an accept list it cannot match renders its denied page.
    return (
      <RoleBasedGuard
        hasContent
        currentRole={role ?? 'none'}
        acceptRoles={[]}
        sx={[DeniedPageStyle, ...(Array.isArray(sx) ? sx : [sx])]}
      >
        {children}
      </RoleBasedGuard>
    );
  }

  return <>{children}</>;
}

/**
 * Inline gate for a control: renders `children` only when the role may perform `action`, or `fallback` when it
 * may not. Keeps JSX free of role names, which belong in role-matrix.ts alone.
 */
export function Can({
  action,
  children,
  fallback = null,
}: {
  action: RoleAction;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const allowed = useRole().can(action);
  return <>{allowed ? children : fallback}</>;
}

/**
 * Centre the denied page in the viewport below the app header, whichever route rendered it, so every
 * role-gated page refuses the same way instead of sitting at the top of an otherwise empty screen.
 */
const DeniedPageStyle = {
  minHeight: {
    xs: 'calc(100vh - var(--layout-header-mobile-height) - 48px)',
    md: 'calc(100vh - var(--layout-header-desktop-height) - 48px)',
  },
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  py: 3,
  '& .MuiTypography-root, & svg': { mx: 'auto' },
} as const;

export { ORGANIZATION_ROLES };
